import { Injectable, inject } from '@angular/core';
import { AWSLambda } from '../../AWSLambda.service';
import { FilterField, FilterType } from '../generic-list/filter-field.model';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export interface BtbData {
  numClients: number;
  facturasLiquidadas: any[];
  facturasPendientes: any[];
  condicionesComision: string[];
  userTransactions: any[];
  totalBilling: number;
  pendingTotal: number;
}

@Injectable({
  providedIn: 'root'
})
export class BtbDataService {
  private lambda = inject(AWSLambda);
  private translate = inject(TranslateService);

  async loadClientCount(userId?: string): Promise<number> {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'clientCount',
          ...(userId && { userId })
        }
      });

      return response.clientCount || 0;
    } catch (error) {
      console.error('Error loading client count:', error);
      return 0;
    }
  }

  async loadAllInvoices(userId?: string): Promise<any[]> {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'allInvoices',
          ...(userId && { userId })
        }
      });

      return response.allInvoices || [];
    } catch (error) {
      console.error('Error loading all invoices:', error);
      return [];
    }
  }

  async loadBillingTotals(userId?: string, startDate?: string, endDate?: string): Promise<{totalBilling: number, pendingBilling: number}> {
    try {
      const queryParams: any = {
        method: 'getBillingTotals',
        ...(userId && { userId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      };

      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: queryParams
      });

      return {
        totalBilling: response.totalBilling || 0,
        pendingBilling: response.pendingBilling || 0
      };
    } catch (error) {
      console.error('Error loading billing totals:', error);
      return {
        totalBilling: 0,
        pendingBilling: 0
      };
    }
  }


  async loadConditions(userId?: string): Promise<string[]> {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'conditions',
          ...(userId && { userId })
        }
      });

      return response.conditions || [];
    } catch (error) {
      console.error('Error loading conditions:', error);
      return [];
    }
  }

  calculateTotals(facturasLiquidadas: any[], facturasPendientes: any[]): { totalBilling: number; pendingTotal: number } {
    const totalBilling = facturasLiquidadas.reduce((total, factura) => {
      return total + (factura.amount || 0);
    }, 0);

    const pendingTotal = facturasPendientes.reduce((total, factura) => {
      return total + (factura.amount || 0);
    }, 0);

    return { totalBilling, pendingTotal };
  }

  async initializeFilterFields(allInvoices: any[], userTransactions: any[] = []): Promise<{
    filterFields: FilterField[];
    filterTransactionsFields: FilterField[];
  }> {
    const invoiceNumberLabel = await firstValueFrom(this.translate.get('BTB.DASHBOARD.FILTERS.INVOICE_NUMBER'));
    const dateLabel = await firstValueFrom(this.translate.get('BTB.DASHBOARD.FILTERS.DATE'));
    const amountLabel = await firstValueFrom(this.translate.get('BTB.DASHBOARD.FILTERS.AMOUNT'));
    const statusLabel = await firstValueFrom(this.translate.get('BTB.DASHBOARD.TABLE.STATUS'));

    const maxAmount = allInvoices.length > 0
      ? Math.max(...allInvoices.map(f => f.amount))
      : 1000;

    const maxAmountWithPadding = Math.ceil(maxAmount * 1.2);

    const filterFields: FilterField[] = [
      { key: 'id', type: 'text' as FilterType, label: invoiceNumberLabel },
      { key: 'createdAt', type: 'date-range' as FilterType, label: dateLabel },
      { key: 'start_date', type: 'date-range' as FilterType, label: await firstValueFrom(this.translate.get('BTB.DASHBOARD.TABLE.START_DATE')) },
      { key: 'end_date', type: 'date-range' as FilterType, label: await firstValueFrom(this.translate.get('BTB.DASHBOARD.TABLE.END_DATE')) },
      { key: 'amount', type: 'range-slider' as FilterType, label: amountLabel, min: 0, max: maxAmountWithPadding },
      { key: 'status', type: 'select' as FilterType, label: statusLabel, options: [
        { key: 'PENDING', value: await firstValueFrom(this.translate.get('BTB.DASHBOARD.INVOICE_STATUS.PENDING')) },
        { key: 'PENDING_CONFIRMED', value: await firstValueFrom(this.translate.get('BTB.DASHBOARD.INVOICE_STATUS.PENDING_CONFIRMED')) },
        { key: 'SETTLED', value: await firstValueFrom(this.translate.get('BTB.DASHBOARD.INVOICE_STATUS.SETTLED')) },
        { key: 'CANCELLED', value: await firstValueFrom(this.translate.get('BTB.DASHBOARD.INVOICE_STATUS.CANCELLED')) }
      ]}
    ];

    const uniqueTypes = [...new Set(userTransactions.map(t => t.Tipo).filter(tipo => tipo && tipo.trim() !== ''))];
    const tipoOptions = uniqueTypes.map(tipo => ({ key: tipo, value: tipo }));

    const importes = userTransactions.map(t => t.monto_bruto || 0).filter(importe => importe > 0);
    const maxImporte = importes.length > 0 ? Math.max(...importes) : 50;
    const maxImporteWithPadding = Math.ceil(maxImporte * 1.2);

    const filterTransactionsFields: FilterField[] = [
      { 
        key: 'Tipo', 
        type: 'select' as FilterType, 
        label: 'Tipo',
        options: tipoOptions
      },
      { key: 'monto_bruto', type: 'range-slider' as FilterType, label: 'Importe', min: 0, max: maxImporteWithPadding }
    ];

    return {
      filterFields,
      filterTransactionsFields
    };
  }

  async getuserRefTransactions(userId: string, fechaini: string, fechafin: string): Promise<any[]> {
  try {
    const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
      queryStringParameters: {
        method: 'getuserRefTransactions',
        userId: userId,
        fechaini: fechaini,
        fechafin: fechafin
      }
    });

    // Siempre crear una nueva referencia de array para forzar la detección de cambios
    return response.transactions ? [...response.transactions] : [];
  } catch (error) {
    console.error('Error loading conditions:', error);
    // Siempre crear una nueva referencia de array vacío
    return [];
  }
}
}
